import React, { useCallback, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { isEqual } from 'lodash';

import { ApolloFetchPolicy } from 'common/apollo/constants';
import useSequentialPolling from 'common/components/SequentialPoller/useSequentialPolling';
import usePollingContextSubscription from 'common/components/withPollingContext/usePollingContextSubscription';
import { useCustomMemo } from 'common/helpers/useCustomMemo';

import type { SourceProps } from '../Source';
import useEntityListWatcher from '../utils/EntityListMatcher/useEntityListWatcher';
import useStoreHelper from '../utils/WithStoreHelpers/useStoreHelper';

export const SOURCE_POLL_INTERVAL = 5 * 1000;

export type PollingSourceProps<EntityResponse, QueryResponse> = Omit<
  SourceProps<EntityResponse, QueryResponse>,
  'subscriptions'
>;

export default function PollingSource<EntityResponse, QueryResponse>({
  name,
  children,
  query,
  skip,
  queryVariables: propsQueryVariables,
  onEntityCreated,
  onEntityDeleted,
  onEntityLoaded,
  onEntityUpdated,
  selectEntities,
  selectStaleEntities,
  selectEntityId,
  selectSyncOn,
}: PollingSourceProps<EntityResponse, QueryResponse>) {
  const queryVariables = useCustomMemo(
    {
      fetch: true,
      sync: true,
      id: [],
      ...propsQueryVariables,
    },
    isEqual
  );

  const queryResult = useQuery(query, {
    skip,
    variables: queryVariables,
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY,
  });

  const storeHelpers = useStoreHelper({
    selectEntityId,
    selectSyncOn,
    selectEntities,
    query,
    name,
    loading: queryResult.loading,
    queryVariables,
  });

  const handleEntityCreated = useCallback(
    (newEntity: EntityResponse) => {
      onEntityCreated?.(newEntity, storeHelpers);
    },
    [onEntityCreated, storeHelpers]
  );

  const handleEntityLoaded = useCallback(
    (newEntity: EntityResponse) => {
      onEntityLoaded?.(newEntity, storeHelpers);
    },
    [onEntityLoaded, storeHelpers]
  );

  const handleEntityUpdated = useCallback(
    (currEntity: EntityResponse, prevEntity: EntityResponse) => {
      onEntityUpdated?.(currEntity, prevEntity, storeHelpers);
    },
    [onEntityUpdated, storeHelpers]
  );

  const handleEntityDeleted = useCallback(
    (deletedEntity: EntityResponse) => {
      onEntityDeleted?.(deletedEntity, storeHelpers);
    },
    [onEntityDeleted, storeHelpers]
  );

  const { refetch, data } = queryResult;

  const handlePoll = useCallback(
    () => refetch({ ...queryVariables, id: selectEntities(data).map(selectEntityId) }),
    [queryVariables, selectEntities, selectEntityId, data, refetch]
  );

  const entities = useMemo(() => selectEntities(queryResult.data), [queryResult.data, selectEntities]);
  const staleEntities = useMemo(() => selectStaleEntities(queryResult.data), [queryResult.data, selectStaleEntities]);

  useEntityListWatcher({
    name,
    loading: queryResult.loading,
    entities,
    staleEntities,
    syncOnDelete: true,
    skip,
    selectEntityId,
    selectSyncOn,
    onEntityCreated: handleEntityCreated,
    onEntityUpdated: handleEntityUpdated,
    onEntityDeleted: handleEntityDeleted,
    onEntityLoaded: handleEntityLoaded,
  });

  const { startPolling, stopPolling } = useSequentialPolling({
    pollInterval: SOURCE_POLL_INTERVAL,
    query: handlePoll,
    queryVariables,
  });

  usePollingContextSubscription({ startActions: [startPolling], stopActions: [stopPolling] });

  return <>{children(entities, storeHelpers, queryResult)}</>;
}
